import { Button, Col, Form, Row, Select, Image, Steps } from "antd"
import React, { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Trash2 as TrashIcon, Plus as PlusIcon, ChevronRight as RightIcon } from "react-feather"
import { currencyEnumArray, Employee, Workflow, WorkflowConditionEnum, WorkflowRule, WorkflowTriggerEnum } from "@finway-group/shared/lib/models"
import UserImage from "Components/UserImage"
import CurrencyInput from "Components/currencyInput"
import { Currencies } from "@finway-group/shared/lib/consts"
import { useLoggedInUserProfile } from "Shared/hooks/user.hooks"
import { RESPONSIBLE_USER_APPROVER, SUPERIOR_APPROVER } from "./workflow.form"
import { EmployeeService } from "Shared/services"
import { getPopupAnchor } from "Shared/utils"
import { FormInstance } from "antd/lib/form"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"

interface WorkflowFormRulesInterface {
    workflow: Workflow
    updateWorkflow: (data: any, shouldValidate?: boolean, nameList?: string[]) => void
    possibleApprovers: Employee[]
    formInstance: FormInstance
    rules: any
}

export const WorkflowFormRules: React.FC<WorkflowFormRulesInterface> = ({ workflow, updateWorkflow, possibleApprovers, rules, formInstance }) => {
    const { t } = useTranslation()
    const loggedInUser = useLoggedInUserProfile()
    const autoApprover = EmployeeService.getAutoApprover()

    const [isAutoApproverSelected, setIsAutoApproverSelected] = useStateIfMounted(formInstance.getFieldValue("rules")?.[0]?.approverData)

    const anchorRef = useRef(null)

    const updateRule = (index: number, data: any, triggerValidation = false, nameList: string[] = []) =>
        updateWorkflow({ rules: workflow.rules.map((rule: WorkflowRule, i: number) => (i === index ? { ...rule, ...data } : rule)) }, triggerValidation, nameList)

    const onFilterPossibleApprovers = (input: any, option: any) => {
        if (!option?.value) return false
        const employee = possibleApprovers.find(({ id }: any) => id === option.value)
        const label = employee ? `${employee?.firstName} ${employee?.lastName}` : ""

        return label.toLocaleLowerCase().indexOf(input.toLowerCase()) >= 0
    }

    // Sets/unsets the state variable "isAutoApproverSelected"
    useEffect(() => {
        const rules = formInstance.getFieldValue("rules")
        const firstRuleApproverId = formInstance.getFieldValue("rules")?.[0]?.approverData
        if (firstRuleApproverId === autoApprover.id) {
            rules[0].secondApprover = undefined
            rules[0].condition = undefined
            formInstance.setFieldsValue({ rules })
            setIsAutoApproverSelected(true)
        } else if (isAutoApproverSelected === true) setIsAutoApproverSelected(false)
    }, [formInstance.getFieldValue("rules")?.[0]?.approverData])

    const responsibleEmployeeSelect = (
        <Select.Option value={RESPONSIBLE_USER_APPROVER}>
            <div className="flex items-center">
                <UserImage user={autoApprover} />
                <span>{t("input:workflow.responsible_employee")}</span>
            </div>
        </Select.Option>
    )
    const superiorEmployeeSelect = (
        <Select.Option value={SUPERIOR_APPROVER}>
            <div className="flex items-center">
                <UserImage user={autoApprover} />
                <span>{t("input:workflow.superior")}</span>
            </div>
        </Select.Option>
    )

    return (
        <Form.List name="rules">
            {(fields, { add, remove }) => (
                <div className="workflows workflows-rules" ref={anchorRef}>
                    <Steps className="" direction="vertical" size="small" current={-1}>
                        {fields.map((field, index) => {
                            const rule = workflow.rules[index]

                            if (!rule) return <></>

                            return (
                                <Steps.Step
                                    key={index}
                                    status="process"
                                    description={
                                        <Row className="w-full" key={index} gutter={[8, 8]}>
                                            {rule.condition === WorkflowConditionEnum.IF && (
                                                <>
                                                    <Col lg={5} md={8} xs={10}>
                                                        <Form.Item name={[field.name, "amount"]} label={t("input:workflow.conditions.if_amount_bigger")} required>
                                                            <CurrencyInput
                                                                value={0}
                                                                min={0}
                                                                currency={rule.currency || loggedInUser.settings.globalCurrency}
                                                                onChange={(_a, value) => updateRule(index, { amount: value })}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col lg={4} md={6} xs={4}>
                                                        <Form.Item
                                                            name={[field.name, "currency"]}
                                                            label={t("input:request.currency")}
                                                            key="currency"
                                                            initialValue={rule.currency || loggedInUser.settings.globalCurrency}
                                                        >
                                                            <Select
                                                                showSearch
                                                                onChange={(v) => updateRule(index, { currency: v })}
                                                                getPopupContainer={getPopupAnchor(anchorRef.current)}
                                                            >
                                                                {currencyEnumArray.map((currencyValue, index) => (
                                                                    <Select.Option key={index} value={currencyValue} label={currencyValue}>
                                                                        <div className="space-x-10">
                                                                            <Image
                                                                                className="align-middle"
                                                                                width={20}
                                                                                preview={false}
                                                                                src={`data:image/png;base64,${Currencies[currencyValue].flag}`}
                                                                            />
                                                                            <span className="text-text">{currencyValue}</span>
                                                                        </div>
                                                                    </Select.Option>
                                                                ))}
                                                            </Select>
                                                        </Form.Item>
                                                    </Col>
                                                    <Col>
                                                        <Button className="mt-30">{t("input:workflow.conditions.then")}</Button>
                                                    </Col>
                                                </>
                                            )}

                                            <Col lg={9} md={rule.condition ? 20 : 9} xs={rule.condition ? 18 : 24}>
                                                <Form.Item name={[field.name, "approverData"]} label={t("input:workflow.approver")} required rules={rules.approver}>
                                                    <Select
                                                        showSearch
                                                        filterOption={onFilterPossibleApprovers}
                                                        onChange={(v) => updateRule(index, { approverData: v }, true, [`rules.${index}.secondApprover`])}
                                                        getPopupContainer={getPopupAnchor(anchorRef.current)}
                                                    >
                                                        {workflow.triggerType === WorkflowTriggerEnum.COST_CENTER && responsibleEmployeeSelect}
                                                        {superiorEmployeeSelect}
                                                        {[...(index === 0 ? possibleApprovers : possibleApprovers.filter((approver) => approver.id !== autoApprover.id))].map(
                                                            (employee: any, index: number) => (
                                                                <Select.Option key={index} value={employee.id}>
                                                                    <div className="flex items-center">
                                                                        <UserImage user={employee} />
                                                                        <span>{`${employee.firstName} ${employee.lastName}`}</span>
                                                                    </div>
                                                                </Select.Option>
                                                            )
                                                        )}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            {(!rule.condition || rule.condition === WorkflowConditionEnum.OR) && (!isAutoApproverSelected || index !== 0) && (
                                                <Col className="mt-30" onClick={() => updateRule(index, { condition: WorkflowConditionEnum.OR })}>
                                                    <Button className="w-50">{t("input:workflow.conditions.or")}</Button>
                                                </Col>
                                            )}
                                            {(!rule.condition || rule.condition === WorkflowConditionEnum.AND) && (!isAutoApproverSelected || index !== 0) && (
                                                <Col className="mt-30" onClick={() => updateRule(index, { condition: WorkflowConditionEnum.AND })}>
                                                    <Button className="w-50">{t("input:workflow.conditions.and")}</Button>
                                                </Col>
                                            )}
                                            {!rule.condition && (!isAutoApproverSelected || index !== 0) && (
                                                <Col
                                                    className="mt-30"
                                                    onClick={() =>
                                                        updateRule(index, { condition: WorkflowConditionEnum.IF, amount: 0, currency: loggedInUser.settings.globalCurrency })
                                                    }
                                                >
                                                    <Button>
                                                        <RightIcon />
                                                        <span>€</span>
                                                    </Button>
                                                </Col>
                                            )}
                                            {[WorkflowConditionEnum.AND, WorkflowConditionEnum.OR].includes(rule.condition) && (!isAutoApproverSelected || index !== 0) && (
                                                <Col lg={9} md={rule.condition ? 20 : 9} xs={rule.condition ? 18 : 9}>
                                                    <Form.Item name={[field.name, "secondApprover"]} label={t("input:workflow.approver")} required rules={rules.secondApprover}>
                                                        <Select
                                                            showSearch
                                                            filterOption={onFilterPossibleApprovers}
                                                            onChange={(v) => updateRule(index, { secondApprover: v }, true, [`rules.${index}.approver`])}
                                                            getPopupContainer={getPopupAnchor(anchorRef.current)}
                                                        >
                                                            {workflow.triggerType === WorkflowTriggerEnum.COST_CENTER && responsibleEmployeeSelect}
                                                            {superiorEmployeeSelect}
                                                            {possibleApprovers
                                                                .filter((approver) => approver.id !== autoApprover.id)
                                                                .map((employee: any, index: number) => {
                                                                    return (
                                                                        <Select.Option key={index} value={employee.id}>
                                                                            <div className="flex items-center" id={`requestedBy-option-${index}`}>
                                                                                <UserImage user={employee} />
                                                                                <span>{`${employee.firstName} ${employee.lastName}`}</span>
                                                                            </div>
                                                                        </Select.Option>
                                                                    )
                                                                })}
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                            )}
                                            {(fields.length > 1 || rule.condition) && (
                                                <Col className="mt-30">
                                                    <Button
                                                        onClick={() => {
                                                            if (fields.length > 1) {
                                                                remove(index)
                                                                updateWorkflow({ rules: workflow.rules.filter((_rule: WorkflowRule, i: number) => i !== index) })
                                                            } else {
                                                                updateRule(index, {
                                                                    approverData: undefined,
                                                                    condition: undefined,
                                                                    secondApprover: undefined,
                                                                    currency: undefined,
                                                                    amount: undefined,
                                                                })
                                                            }
                                                        }}
                                                    >
                                                        <TrashIcon />
                                                    </Button>
                                                </Col>
                                            )}
                                        </Row>
                                    }
                                />
                            )
                        })}
                    </Steps>
                    <Row gutter={[8, 0]}>
                        <Button
                            onClick={() => {
                                add()
                                updateWorkflow({ rules: [...workflow.rules, {}] })
                            }}
                        >
                            <PlusIcon />
                            <span className="ml-2">{t("action:workflow.next_step")}</span>
                        </Button>
                    </Row>
                </div>
            )}
        </Form.List>
    )
}

export default WorkflowFormRules
